import {
  Component,
  Inject,
  Injector,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormFieldModel, FormModel } from '@alfresco/adf-core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil } from 'rxjs/operators';

import { IReassignTaskDialogData } from '../../model';
import {
  TaskListDefinitionDataModel,
  TaskListDefinitionDataModelResponse,
} from '../../components/shared/models';
import { ProcessListService } from '../../components/shared/services/process-list.service';
import { WkTaskListService } from '../../services/wk-task-list.service';
import {
  ReloadDocumentListAction,
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '../../store/actions/app.custom-actions';
import { AppStore } from '../../store/states/app.state';

@Component({
  selector: 'wk-reassign-task-dialog',
  templateUrl: 'wk-reassign-task.dialog.html',
  styleUrls: ['./wk-reassign-task.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WkReassignTaskDialogComponent implements OnInit {
  public workflowName = '';
  public currentStepName = '';
  public newAssigneeId = '';
  public reassignTaskGroup: FormGroup;
  public newAssigneeTaskField: FormFieldModel;
  public nextStepList$: Observable<TaskListDefinitionDataModel>;
  public diagramImgUrl: string;
  public submitClicked = false;
  public readonly expandedFilterPanelHeight = '48px';

  private readonly ngOnDestroy$ = new Subject<void>();
  private readonly processListService: ProcessListService;
  private readonly wkTaskListService: WkTaskListService;
  private readonly store: Store<AppStore> = inject(Store);

  taskDefinition: TaskListDefinitionDataModelResponse;

  constructor(
    public dialogRef: MatDialogRef<WkReassignTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IReassignTaskDialogData,
    injector: Injector,
    private readonly translateService: TranslateService
  ) {
    this.processListService =
      injector.get<ProcessListService>(ProcessListService);
    this.wkTaskListService = injector.get<WkTaskListService>(WkTaskListService);
  }

  ngOnInit(): void {
    this.reassignTaskGroup = new FormGroup({
      nextStepKey: new FormControl('', Validators.required),
      newAssignee: new FormControl('', Validators.required),
      currentStepName: new FormControl(
        { value: '', disabled: true },
        Validators.required
      ),
    });

    if (this.data.selected.length && !this.data.invalidDocumentsList.length) {
      const taskList = this.data.selected.map((task) => task.stepId);

      this.fetchDiagramImgUrl(this.data.selected[0].workflowId);
      this.workflowName = this.data.selected[0].workflowName;
      this.newAssigneeId = this.data.selected[0].stepAssignee;
      this.setCurrentStepName(this.data.selected[0].stepName);

      const stepsData$ = this.wkTaskListService
        .getTasksDataSteps(taskList)
        .pipe(shareReplay(1));

      stepsData$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((response) => {
        this.taskDefinition = response;
        const resolvedProcessName = (
          response?.results?.[0] as TaskListDefinitionDataModel & {
            processName?: string;
          }
        )?.processName;
        const resolvedCurrentStep = response?.results?.[0]?.current?.name;

        if (resolvedProcessName) {
          this.workflowName = resolvedProcessName;
        }

        if (!this.currentStepName && resolvedCurrentStep) {
          this.setCurrentStepName(resolvedCurrentStep);
        }
      });

      this.nextStepList$ = stepsData$.pipe(
        map((response) => response.results[0]),
        catchError(() => {
          this.store.dispatch(
            new SnackbarErrorAction(
              'TASK_REASSIGNMENT.DIALOG.ERRORS.FETCH_STEPS'
            )
          );
          return EMPTY;
        }),
        takeUntil(this.ngOnDestroy$)
      );
      this.newAssigneeTaskField = new FormFieldModel(new FormModel(), {
        id: 'newAssignee',
        value: this.newAssigneeId,
        className: 'reassign-task-people-widget',
        name: this.translateService.instant(
          'TASK_REASSIGNMENT.DIALOG.SUCCESS.FORM.NEW_ASSIGNEE'
        ),
        placeholder: this.translateService.instant(
          'TASK_REASSIGNMENT.DIALOG.SUCCESS.FORM.NEW_ASSIGNEE_PLACEHOLDER'
        ),
        tooltip: this.translateService.instant(
          'TASK_REASSIGNMENT.DIALOG.SUCCESS.FORM.NEW_ASSIGNEE'
        ),
        readOnly: false,
        required: true,
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  public setAssigneeId(assignee: number): void {
    this.reassignTaskGroup.get('newAssignee').setValue(assignee);
    this.reassignTaskGroup.get('newAssignee').updateValueAndValidity();
  }

  public onAssigneeInputChange(peopleWidget: {
    searchTerm?: { value: string };
  }): void {
    const inputValue = peopleWidget?.searchTerm?.value || '';
    if (!inputValue.trim()) {
      this.reassignTaskGroup.get('newAssignee').setValue('');
      this.reassignTaskGroup.get('newAssignee').updateValueAndValidity();
    }
  }

  public setCurrentStepName(stepName: string): void {
    this.currentStepName = stepName;
    const control = this.reassignTaskGroup.get('currentStepName');
    control.enable();
    control.setValue(stepName);
    control.disable();
    control.updateValueAndValidity();
  }

  isFormValid(): boolean {
    return this.reassignTaskGroup.invalid;
  }

  confirmReassignTask(): void {
    const bulkTaskList = {
      taskIds: this.data.selected.map((task) => task.stepId),
      nextStep: this.reassignTaskGroup.get('nextStepKey').value,
      assignee: this.reassignTaskGroup.get('newAssignee').value,
    };
    this.submitClicked = true;

    this.wkTaskListService
      .setTaskReassignmentBulk(bulkTaskList)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe({
        next: (response) => {
          this.store.dispatch(
            new SnackbarInfoAction(
              'TASK_REASSIGNMENT.DIALOG.SUCCESS.SUCCESS_MESSAGE'
            )
          );
          this.store.dispatch(new ReloadDocumentListAction());
          this.dialogRef.close(response);
        },
        error: () => {
          this.submitClicked = false;
          this.store.dispatch(
            new SnackbarErrorAction(
              'TASK_REASSIGNMENT.DIALOG.ERRORS.REASSIGN_BULK'
            )
          );
        },
      });
  }

  private fetchDiagramImgUrl(processInstanceId: string): void {
    if (!processInstanceId) {
      return;
    }

    this.processListService
      .getProcessInstanceDiagram(processInstanceId)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((imageData) => {
        this.diagramImgUrl = this.getDiagramImgUrl(imageData);
      });
  }

  private getDiagramImgUrl(imageData: Blob): string {
    this.diagramImgUrl = URL.createObjectURL(imageData);
    return this.diagramImgUrl;
  }

  public formatDocumentPath(document: any): string {
    const elements = document?.path?.elements || [];
    const lastThree = elements
      .slice(-3)
      .map((el: any) => el?.name || '')
      .filter(Boolean);
    return [...lastThree, document?.fileName || ''].join(' > ');
  }

  public getDocumentsByReason(reason: string): any[] {
    return (this.data.invalidDocumentsList || []).filter(
      (doc) => doc.invalidReason === reason
    );
  }
}
